import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarBlank, MapPin, X } from "@phosphor-icons/react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const isoToDate = (iso) => new Date(iso + "T00:00:00");

export function UserRestrictionsFields({ value, onChange, provinces, testPrefix }) {
  const [q, setQ] = useState("");
  const [dateOpen, setDateOpen] = useState(false);
  const selected = value.allowed_provinces || [];
  const nameOf = useMemo(() => new Map(provinces.map((p) => [p.id, p.name])), [provinces]);
  const filtered = provinces.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    onChange({ ...value, allowed_provinces: next });
  };

  return (
    <div className="border border-white/10 bg-[#121212]/50 p-3 space-y-3" data-testid={`${testPrefix}-restrictions`}>
      <p className="label-micro text-[#00E5FF]">RESTRICCIONES DE ACCESO</p>
      <div>
        <Label className="label-micro">Provincias permitidas (origen o destino)</Label>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={`${testPrefix}-provinces-trigger`}
              className="w-full mt-1 min-h-9 px-3 py-1.5 text-left text-sm border border-white/10 bg-[#121212] hover:border-[#00E5FF]/60 transition-colors flex items-center gap-2"
            >
              <MapPin size={14} className="text-white/40 shrink-0" />
              <span className={selected.length ? "text-white" : "text-white/40"}>
                {selected.length ? selected.map((id) => nameOf.get(id) || id).join(", ") : "Sin restricción (toda España)"}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0 rounded-none bg-[#0A0A0A] border-white/10 text-white" align="start">
            <div className="p-2 border-b border-white/10">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar provincia…"
                className="h-7 rounded-none bg-[#121212] border-white/10 text-xs"
                data-testid={`${testPrefix}-provinces-search`}
              />
            </div>
            <div className="max-h-64 overflow-auto">
              {filtered.map((p) => (
                <label
                  key={p.id}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-white/[0.04] cursor-pointer"
                  data-testid={`${testPrefix}-province-option-${p.id}`}
                >
                  <Checkbox checked={selected.includes(p.id)} onCheckedChange={() => toggle(p.id)} />
                  <span className="flex-1">{p.name}</span>
                  <span className="font-mono text-[10px] text-white/30">{p.id}</span>
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => onChange({ ...value, allowed_provinces: [] })}
            className="mt-1 text-[11px] font-mono text-white/40 hover:text-white inline-flex items-center gap-1"
            data-testid={`${testPrefix}-provinces-clear`}
          >
            <X size={10} /> Quitar restricción geográfica
          </button>
        )}
      </div>
      <div>
        <Label className="label-micro">Acceso válido hasta (incluido)</Label>
        <div className="flex items-center gap-2 mt-1">
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid={`${testPrefix}-valid-until`}
                className="flex-1 h-9 px-3 text-left text-sm border border-white/10 bg-[#121212] hover:border-[#00E5FF]/60 transition-colors flex items-center gap-2 font-mono"
              >
                <CalendarBlank size={14} className="text-white/40" />
                <span className={value.valid_until ? "text-white" : "text-white/40"}>
                  {value.valid_until ? format(isoToDate(value.valid_until), "dd/MM/yyyy") : "Sin caducidad"}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="p-0 w-auto rounded-none bg-[#0A0A0A] border-white/10 text-white">
              <Calendar
                mode="single"
                locale={es}
                disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
                weekStartsOn={1}
                selected={value.valid_until ? isoToDate(value.valid_until) : undefined}
                defaultMonth={value.valid_until ? isoToDate(value.valid_until) : new Date()}
                onSelect={(d) => {
                  if (!d) return;
                  onChange({ ...value, valid_until: format(d, "yyyy-MM-dd") });
                  setDateOpen(false);
                }}
                className="bg-[#0A0A0A]"
              />
            </PopoverContent>
          </Popover>
          {value.valid_until && (
            <button
              type="button"
              onClick={() => onChange({ ...value, valid_until: "" })}
              className="text-[11px] font-mono text-white/40 hover:text-white whitespace-nowrap"
              data-testid={`${testPrefix}-valid-until-clear`}
            >
              Sin caducidad
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AccessBadge({ user, provinceNames }) {
  if (user.role === "admin") return <span className="text-white/30 font-mono text-[11px]">Completo</span>;
  const provs = user.allowed_provinces || [];
  const today = new Date().toISOString().slice(0, 10);
  const expired = user.valid_until && user.valid_until < today;
  return (
    <div className="flex flex-col gap-0.5 font-mono text-[11px]" data-testid={`access-badge-${user.email}`}>
      <span className={provs.length ? "text-[#00E5FF]" : "text-white/40"}>
        {provs.length ? provs.map((id) => provinceNames.get(id) || id).join(", ") : "Toda España"}
      </span>
      {user.valid_until ? (
        <span className={expired ? "text-[#F44336]" : "text-white/50"}>
          {expired ? "CADUCADO · " : "hasta "}
          {new Date(user.valid_until + "T00:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" })}
        </span>
      ) : (
        <span className="text-white/30">Sin caducidad</span>
      )}
    </div>
  );
}
